import { assertUnreachable, assertUnionArrayEquals } from "@/utils";
import {
  Condition,
  Keys,
  KeyboardLed,
  Repeat,
  Interpolation,
  KeyAction,
} from "@/keyboard";

type C = Condition;
type K = Keys | null;

export type CondAlways = Extract<C, "Always">;
export type CondLed = Extract<C, { Led: unknown }>;
export type CondUsbOn = Extract<C, "UsbOn">;
export type CondRole = Extract<C, { Role: unknown }>;
export type CondPressed = Extract<C, "Pressed">;
export type CondKeyAction = Extract<C, { KeyAction: unknown }>;
export type CondKeyPressed = Extract<C, { KeyPressed: unknown }>;
export type CondLayer = Extract<C, { Layer: unknown }>;
export type CondBootloaderAllowed = Extract<C, "BootloaderAllowed">;
export type CondNot = Extract<C, { Not: unknown }>;
export type CondAnd = Extract<C, { And: unknown }>;
export type CondOr = Extract<C, { Or: unknown }>;

export type KeysAll = Extract<K, null>;
export type KeysRows = Extract<K, { Rows: unknown }>;
export type KeysCols = Extract<K, { Cols: unknown }>;
export type KeysKeys = Extract<K, { Keys: unknown }>;

// Type guards for action variants
const has = (a: unknown, prop: string): boolean =>
  typeof a == "object" && a != null && prop in a;

export const isCondAlways = (c: C): c is CondAlways => c == "Always";
export const isCondLed = (c: C): c is CondLed => has(c, "Led");
export const isCondUsbOn = (c: C): c is CondUsbOn => c == "UsbOn";
export const isCondRole = (c: C): c is CondRole => has(c, "Role");
export const isCondPressed = (c: C): c is CondPressed => c == "Pressed";
export const isCondKeyAction = (c: C): c is CondKeyAction =>
  has(c, "KeyAction");
export const isCondKeyPressed = (c: C): c is CondKeyPressed =>
  has(c, "KeyPressed");
export const isCondLayer = (c: C): c is CondLayer => has(c, "Layer");
export const isCondBootloaderAllowed = (c: C): c is CondBootloaderAllowed =>
  c == "BootloaderAllowed";
export const isCondNot = (c: C): c is CondNot => has(c, "Not");
export const isCondAnd = (c: C): c is CondAnd => has(c, "And");
export const isCondOr = (c: C): c is CondOr => has(c, "Or");

export const isKeysAll = (k: K): k is KeysAll => k == null;
export const isKeysRows = (k: K): k is KeysRows => has(k, "Rows");
export const isKeysCols = (k: K): k is KeysCols => has(k, "Cols");
export const isKeysKeys = (k: K): k is KeysKeys => has(k, "Keys");

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type ConditionType =
  | Extract<C, string>
  | KeysOfUnion<Exclude<C, string>>;
export type KeysType = "All" | KeysOfUnion<Exclude<K, "All">>;

export type ConditionLogic = Extract<
  Condition,
  { Not: unknown } | { And: unknown } | { Or: unknown }
>;
export type ConditionValue = Exclude<Condition, ConditionLogic>;

export type ConditionTypeLogic = KeysOfUnion<ConditionLogic>;
export type ConditionTypeValue = Exclude<ConditionType, ConditionTypeLogic>;

export const isCondLogic = (c: C): c is ConditionLogic =>
  ["Not", "And", "Or"].some((k) => has(c, k));

export const isCondTypeLogic = (c: ConditionType): c is ConditionTypeLogic =>
  ["Not", "And", "Or"].includes(c);

export const conditionType = (c: C): ConditionType =>
  isCondAlways(c)
    ? "Always"
    : isCondLed(c)
    ? "Led"
    : isCondUsbOn(c)
    ? "UsbOn"
    : isCondRole(c)
    ? "Role"
    : isCondPressed(c)
    ? "Pressed"
    : isCondKeyAction(c)
    ? "KeyAction"
    : isCondKeyPressed(c)
    ? "KeyPressed"
    : isCondLayer(c)
    ? "Layer"
    : isCondBootloaderAllowed(c)
    ? "BootloaderAllowed"
    : isCondNot(c)
    ? "Not"
    : isCondAnd(c)
    ? "And"
    : isCondOr(c)
    ? "Or"
    : assertUnreachable(c);

export const conditionTypeLogic = (c: ConditionLogic): ConditionTypeLogic =>
  isCondNot(c)
    ? "Not"
    : isCondAnd(c)
    ? "And"
    : isCondOr(c)
    ? "Or"
    : assertUnreachable(c);

export const keysType = (k: K): KeysType =>
  isKeysAll(k)
    ? "All"
    : isKeysRows(k)
    ? "Rows"
    : isKeysCols(k)
    ? "Cols"
    : isKeysKeys(k)
    ? "Keys"
    : assertUnreachable(k);

export const KEYBOARD_LEDS = [
  "NumLock",
  "CapsLock",
  "ScrollLock",
  "Compose",
  "Kana",
] as const;

export const REPEAT = ["Once", "Wrap", "Reflect"] as const;

export const INTERPOLATION = ["Piecewise", "Linear"] as const;

export const KEY_ACTION = [
  "NoOp",
  "Trans",
  "KeyCode",
  "MultipleKeyCodes",
  "MultipleActions",
  "Layer",
  "DefaultLayer",
  "HoldTap",
  "Custom",
] as const;

export const CONDITION_TYPE = [
  "Always",
  "Led",
  "UsbOn",
  "Role",
  "Pressed",
  "KeyAction",
  "KeyPressed",
  "Layer",
  "BootloaderAllowed",
  "Not",
  "And",
  "Or",
] as const;

const _verifyKeyboardLeds = () =>
  assertUnionArrayEquals<KeyboardLed, typeof KEYBOARD_LEDS>(KEYBOARD_LEDS);
const _verifyRepeat = () =>
  assertUnionArrayEquals<Repeat, typeof REPEAT>(REPEAT);
const _verifyInterpolation = () =>
  assertUnionArrayEquals<Interpolation, typeof INTERPOLATION>(INTERPOLATION);
const _verifyKeyAction = () =>
  assertUnionArrayEquals<KeyAction, typeof KEY_ACTION>(KEY_ACTION);
const _verifyConditionType = () =>
  assertUnionArrayEquals<ConditionType, typeof CONDITION_TYPE>(CONDITION_TYPE);
